import cn from 'classnames'
import { IHRDatasetInfo } from 'legacy/lut/classes/helpers/partner360/HistoricalResultsHelper'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import { usePartnerInfo } from 'legacy/lut/classes/helpers/partner360/usePartnerInfo'
import { IFormActualsTargetsSettings } from 'legacy/lut/classes/models/partner360/IFormActualsTargetsSettings'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { formatMoneyInteger } from 'legacy/lut/components/widgets/public/partner360/helpers/formatMoneyInteger'
import { selectGPLoadingStatus } from 'legacy/lut/features/globalPartner'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Switch } from 'core-components/library/checkbox'
import { Checkbox } from 'core-components/library/checkbox'
import { LoaderOverlay } from 'core-components/library/loader'
import { TPopupHorizontal } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import MeasureController from 'core/analytics/MeasureController'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { ActualsTypeButtons, useActualsTypes } from 'partner360/components/actualsTypeButtons'
import { CurrenciesButtons, useCurrencies } from 'partner360/components/currenciesButtons'
import { RowButtons } from 'partner360/components/rowButtons/RowButtons'
import { SellTypeButtons } from 'partner360/components/sellTypeButtons/SellTypeButtons'
import { isSubCategoryMandatory } from 'partner360/entities/planning'
import { IPlanningSmb, PlanningSmbSwitcher } from 'partner360/features/planningSmb'
import { isSectionSettingOn } from 'partner360/helpers/sectionSettings/isSectionSettingOn'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { selectPartnerAccess } from '../../../../../features/partner360/configuration'
import { selectSectionSettings } from '../../../../../features/partner360/sectionSettings'
import { CommentIcon } from '../components/CommentIcon'
import { HelpSection } from '../helpButton'
import { HintComponent } from '../HintComponent'
import { useMarketShare } from '../hooks/useMarketShare'
import { IJBPPlanning, IJBPPlanningItem, SMBFractionTypes } from '../models/IJBPPlanning'

import { WidgetHeaderWithHelpButton } from './../widgetHeader'
import { CategoryHeader } from './components/categoryHeader/CategoryHeader'
import { CategorySubTitle } from './components/categorySubTitle/CategorySubTitle'
import { ExtraConRowsTotal } from './components/extraRows/ExtraConRowsTotal'
import { ExtraFinRowsTotal } from './components/extraRows/ExtraFinRowsTotal'
import { ExtraSobRowsTotal } from './components/extraRows/ExtraSobRowsTotal'
import { actualsWrapper } from './components/fieldWrapper/actualsWrapper'
import { fieldWrapper } from './components/fieldWrapper/fieldWrapper'
import { smbVsEuWrapper } from './components/fieldWrapper/smbVsEuWrapper'
import { PartnersCompletenessField } from './components/partnersCompletenessField/PartnersCompletenessField'
import { SplitPlanningRow } from './components/splitPlanningRow/SplitPlanningRow'
import {
  actualsColor,
  DISPLAY_OPTIONS,
  GLOBAL_TOTAL_CATEGORY,
  HIDE_SOB_FOR_COUNTRY,
  IS_ALWAYS_SHOW_ACTUALS,
  IS_ENABLE_ROUNDING,
  IS_NEW_LNF,
  IS_QUARTER_DEFAULT,
  IS_SHOW_NULL,
  IS_SHOW_PREV_RECALC_ACTUALS,
  OVERALL_CATEGORY,
  PLAN_SECTION_KEY,
  PLANNING_BY_FY_FQ,
  PLANNING_COL_NUMBER,
  PlanningRounding,
  quotaColor,
  ROUNDING_VALUES,
  SOB_FRACTION_DIGITS,
  targetsColor,
} from './config'
import { calcDiff2 } from './helpers/calcDiff'
import { calcPercentage, calcPercentage2 } from './helpers/calcPercentage'
import { getCategoryNAField } from './helpers/getCategoryNAField'
import { getCompletenessItem } from './helpers/getCompletenessItem'
import { getDivisor } from './helpers/getDivisor'
import { getFinSobValue, IGetFinSobValueProps } from './helpers/getFinSobValue'
import { getFractionDigits } from './helpers/getFractionDigits'
import { getIsCollapsedCategory } from './helpers/getIsCollapsedCategory'
import { getNAField } from './helpers/getNAField'
import { getPlanningPeriodColumnColor } from './helpers/getPlanningColumnColor'
import { getSmbEuDiff } from './helpers/getSmbEuDiff'
import { isContractualsByFlag } from './helpers/partnerFlags/isContractualsByFlag'
import {
  IPreparePlanningItems,
  preparePlanningItems,
} from './helpers/prepareData/preparePlanningItems'
import { useDisplaySettings } from './hooks/useDisplaySettings'
import { TCTType, TSplitType } from './models'
import { IGlobalCategory } from './models/IGlobalCategory'
import { IPartnerFlag } from './models/IPartnerFlag'
import { IPartnersCompleteness } from './models/IPartnersCompleteness'

import styles from './CategoryPlanning.module.scss'
import stylesCompensation from 'legacy/lut/components/widgets/public/partner360/Compensation.module.scss'
import stylesTable from 'legacy/lut/components/widgets/public/partner360/PartnerDevicesInfo.module.scss'

interface IProps {
  widgetParams: IObjectAny
  pid: string
  metrics: MeasureController

  planningData: IJBPPlanning[]
  planningSmbData?: IPlanningSmb[]

  formats: IFormActualsTargetsSettings[]
  planningSmbFormats?: IFormActualsTargetsSettings[]
  allDataSets: IHRDatasetInfo[]
  dataSetInfo: IHRDatasetInfo

  isShowFutureYearsPlans: boolean

  isRegionalPlanning: boolean
  setIsRegionalPlanning: (isRegionalPlanning: boolean) => void

  isContractualPlanning: boolean
  setIsContractualPlanning?: (isContractualPlanning: boolean) => void
  isContractualsAvailable: boolean

  isShowOnlyCompletedPlans: boolean
  setIsShowOnlyCompletedPlans?: (isShowOnlyCompletedPlans: boolean) => void

  composedReportMode?: boolean
  composedReportFYear?: number
  fullTotalMode?: boolean

  partnerFlagsState: IPartnerFlag[]

  splitType: TSplitType
  setSplitType: Dispatch<SetStateAction<TSplitType>>

  globalCategory?: IGlobalCategory

  isQuarterlyBusinessReviewMode?: boolean
  quarterlyBusinessReviewNetNdp?: string

  periodFQuarters?: number[]

  setShowActuals: (showActuals: boolean) => void

  showActuals: boolean

  displaySettings: ReturnType<typeof useDisplaySettings>

  isDistributorPlanner: boolean

  roundingValue: PlanningRounding
  setRoundingValue: (roundingValue: PlanningRounding) => void
  roundingValueOriginal: PlanningRounding

  urlActualsValueType?: string

  isOfferingPlanningCompleted?: boolean
  isPlanningSmbEditAllowed?: boolean
  onReloadPlanningData?: () => void | Promise<void>
}

export const TotalPlanning = (props: IProps) => {
  const fQuarters = props.periodFQuarters || [1, 2, 3, 4]
  const sType = '' // Total

  /** -------------------- States ----------------------- */

  const [actualsItems, setActualsItems] = useState<IJBPPlanningItem[]>([])
  const [hideIfEmpty, setHideIfEmpty] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [isDataPresent, setIsDataPresent] = useState(false)
  const [isShowAllSplits, setIsShowAllSplits] = useState(false)
  const [isShowPrevTargets, setIsShowPrevTargets] = useState(false)
  const [splitType, setSplitType] = useState<TSplitType>('Total')
  const [targetsItems, setTargetsItems] = useState<IJBPPlanningItem[]>([])
  const [targetsRollupItems, setTargetsRollupItems] = useState<IJBPPlanningItem[]>([])
  const [actualsRollupItems, setActualsRollupItems] = useState<IJBPPlanningItem[]>([])
  const [actualsPartnerCountItems, setActualsPartnerCountItems] = useState<IJBPPlanningItem[]>([])

  // SMB vs EU split
  const [actualsSmbItems, setActualsSmbItems] = useState<IJBPPlanningItem[]>([])
  const [actualsEuItems, setActualsEuItems] = useState<IJBPPlanningItem[]>([])
  const [targetsSmbItems, setTargetsSmbItems] = useState<IJBPPlanningItem[]>([])
  const [targetsEuItems, setTargetsEuItems] = useState<IJBPPlanningItem[]>([])

  /** -------------------- Selectors ----------------------- */

  const isEditMode = useSelector(selectPublishingMode)
  const { isDistributor, isRegionalDistributor } = useSelector(selectPartnerAccess)
  const sectionSettings = useSelector(selectSectionSettings)
  const gpDataLoadingStatus = useSelector(selectGPLoadingStatus)

  /** -------------------- Hooks ----------------------- */

  const partnerId = usePartnerId(props.widgetParams, props.pid)
  const {
    fyear: partnerFYear,
    partnerInfo,
    canEditPartner,
    buttonCurrType,
  } = usePartnerInfo({
    partnerId,
  })
  const { jbpSettings } = useJbpSettings()
  const { activeCurrency: currency, availableCurrencies } = useCurrencies()
  const { actualsType, actualsTypes, onChangeActualsType } = useActualsTypes()
  const { marketShareData } = useMarketShare({
    partnerId,
    fyear: partnerFYear,
    programType: buttonCurrType,
    section: 'jbp-gtm-priorities',
  })

  const isLoaded = isDataLoaded && gpDataLoadingStatus !== RequestStatus.PENDING

  /** -------------------- Variables ----------------------- */

  const {
    isYearlyPlanning,
    isShowDetailedPlanning,
    isShowPlanAchievement,
    isShowYoYActualsVsActuals,
    isShowYoYActualsVsPlans,
    isShowYoYPlansVsPlans,
    isShowYoYPartnersVsPartners,
    isShowQoQActualsVsActuals,
    isShowQoQActualsVsPlans,
    isShowQoQPlansVsPlans,
    isShowQoQPartnersVsPartners,
    isShowPartnerCount,
    isShowActualsSplit,
    isShowSmbVsEuSplit,
  } = props.displaySettings.settings

  const isEnableRounding = IS_ENABLE_ROUNDING

  const currentValueType = useMemo(() => {
    let calcualtedValueType = actualsType

    if (props.isQuarterlyBusinessReviewMode) {
      calcualtedValueType =
        (props.quarterlyBusinessReviewNetNdp as ActualsValueTypes) || ActualsValueTypes.Ndp
    }

    return calcualtedValueType
  }, [actualsType, props.isQuarterlyBusinessReviewMode, props.quarterlyBusinessReviewNetNdp])

  const fyFq = PLANNING_BY_FY_FQ
  const currentCat = OVERALL_CATEGORY
  const catNAField = getCategoryNAField(currentCat)
  const isShowComments = false
  const fixedComposedReportFYear = props.composedReportFYear ?? jbpSettings.currentFYear
  const fyear = props.composedReportMode ? fixedComposedReportFYear : partnerFYear
  const isAlwaysShowCategoryNA =
    buttonCurrType === ProgramTypes.MPS &&
    partnerInfo &&
    partnerInfo.Country === 'Germany' &&
    currentCat.includes('accessories')
  const isShowCategoryNA =
    !isAlwaysShowCategoryNA && !!props.formats.find((el) => !!el.ShowNotApplicable)
  // TotalPlanning widget use First Category
  const isShowCurrencyCtrl = !props.dataSetInfo.Settings?.HideCurrencyCtrl
  const isShowNetNdpCtrl = !props.dataSetInfo.Settings?.HideNetNdpCtrl
  const isShowActualsCtrl =
    !props.dataSetInfo.Settings?.HideActualsCtrl && !props.composedReportMode
  const isShowSalesTypeCtrl =
    !props.dataSetInfo.Settings?.HideSalesTypeCtrl && !props.composedReportMode
  const isShowCtrlBeforeActuals = isShowCurrencyCtrl || isShowNetNdpCtrl
  const isShowDistributorCtrlFirst = !isShowCtrlBeforeActuals && !isShowActualsCtrl && true
  const isEditAllowed =
    isEditMode && canEditPartner && (!isRegionalDistributor || props.isRegionalPlanning)

  const offeringName = props.fullTotalMode
    ? 'total'
    : buttonCurrType || props.dataSetInfo.ProgramType

  // Category title and tip keys
  const offeringNameLower = offeringName.replace(/\s/g, '').toLowerCase()
  const offeringKey = `jbp-planning-${offeringNameLower}`

  const isHideSoB = useMemo(
    () => partnerInfo?.CountryCode === HIDE_SOB_FOR_COUNTRY,
    [partnerInfo?.CountryCode]
  )

  const isRetailer = useMemo(() => buttonCurrType === ProgramTypes.Retail, [buttonCurrType])

  const globalCategoryKey = useMemo(() => {
    let key = props.globalCategory?.GlobalCategory
    if (
      props.globalCategory &&
      !!props.globalCategory.GlobalSubCategory &&
      props.globalCategory.GlobalSubCategory !== OVERALL_CATEGORY &&
      props.globalCategory.GlobalSubCategory !== GLOBAL_TOTAL_CATEGORY
    ) {
      key = props.globalCategory.GlobalSubCategory
    }

    return key
  }, [props.globalCategory])

  const globalShortKey = useMemo(() => {
    const key = (globalCategoryKey || '').replace(/jbp-/g, '')

    return !key ? `jbp-${offeringNameLower}` : `jbp-${offeringNameLower}-${key}`
  }, [globalCategoryKey, offeringNameLower])

  const totalKey = !partnerInfo?.IsGlobal ? offeringKey : `${globalShortKey}-total`
  const totalTipKey = `${totalKey}-tip`

  // Header key
  const headerSuffix = props.composedReportMode ? '-composed' : '-editor'
  const totalHeaderKey = `${totalKey}${headerSuffix}`

  const isShowContractualSwitcher =
    !!props.composedReportMode && !props.fullTotalMode && !!props.isContractualsAvailable
  const isShowOnlyCompletedPlansSwitcher =
    !!props.composedReportMode && !props.fullTotalMode && false

  const shortPrevFY = (fyear || 2000) - 2001
  const showPrevTargetsParams = [{ key: '{prevFY}', value: shortPrevFY.toString() }]

  const isShowFutureYears = useMemo(() => {
    return (
      props.isShowFutureYearsPlans &&
      fyear > 2024 &&
      !props.isQuarterlyBusinessReviewMode &&
      !isRetailer
    )
  }, [fyear, isRetailer, props.isQuarterlyBusinessReviewMode, props.isShowFutureYearsPlans])

  const isUseContractuals = useMemo(() => {
    const flags = partnerInfo?.PartnerFlags || ''
    const isContrByFlag = isContractualsByFlag(flags, props.partnerFlagsState)
    const isStandartContractualPlanning =
      buttonCurrType === ProgramTypes.CorePrint && (props.isContractualPlanning || isContrByFlag)

    const isDistributorExtendedPlanning =
      props.isDistributorPlanner && !!jbpSettings.distributorExtendedPlanning

    return props.isDistributorPlanner
      ? isDistributorExtendedPlanning
      : isStandartContractualPlanning
  }, [
    partnerInfo?.PartnerFlags,
    props.partnerFlagsState,
    props.isContractualPlanning,
    props.isDistributorPlanner,
    buttonCurrType,
    jbpSettings.distributorExtendedPlanning,
  ])

  const isShowAllSplitsCtrl = useMemo(
    () => isUseContractuals && splitType === 'Total',
    [isUseContractuals, splitType]
  )
  const isShowContrTrans = useMemo(
    () => isShowAllSplitsCtrl && isShowAllSplits,
    [isShowAllSplits, isShowAllSplitsCtrl]
  )
  const isShowTotal = useMemo(
    () => !isUseContractuals || splitType === 'Total',
    [isUseContractuals, splitType]
  )
  const isShowContr = useMemo(
    () =>
      isUseContractuals &&
      ((!isShowAllSplitsCtrl && splitType !== 'Transactional') || isShowContrTrans),
    [isShowAllSplitsCtrl, isShowContrTrans, isUseContractuals, splitType]
  )
  const isShowTrans = useMemo(
    () =>
      isUseContractuals &&
      ((!isShowAllSplitsCtrl && splitType !== 'Contractual') || isShowContrTrans),
    [isShowAllSplitsCtrl, isShowContrTrans, isUseContractuals, splitType]
  )
  const isShowOnlyCompletedPlans = useMemo(
    () => !props.composedReportMode,
    [props.composedReportMode]
  )

  const isShowPrevTargetsCtrl = useMemo(
    () => !IS_NEW_LNF && !props.isQuarterlyBusinessReviewMode,
    [props.isQuarterlyBusinessReviewMode]
  )
  const isExistControlsAfterRounding = useMemo(
    () =>
      (isShowActualsCtrl && !IS_ALWAYS_SHOW_ACTUALS) ||
      (isDistributor && isRegionalDistributor) ||
      isShowPrevTargetsCtrl,
    [isDistributor, isRegionalDistributor, isShowActualsCtrl, isShowPrevTargetsCtrl]
  )

  // Show PrevFY and Cals Actuals logic
  const isShowPrePreYear = true
  const isShowCalcActuals =
    IS_SHOW_PREV_RECALC_ACTUALS &&
    fyear > jbpSettings.fYear &&
    jbpSettings.currentFQuarter === 4 &&
    !props.composedReportMode
  const planningColSpan = useMemo(
    () => PLANNING_COL_NUMBER + (isShowPrePreYear ? 1 : 0) + (isShowCalcActuals ? 1 : 0),
    [isShowPrePreYear, isShowCalcActuals]
  )

  const sobCalcCategoriesParams = useMemo(() => {
    const sobCalcCategories = props.allDataSets
      .filter((el) => el.IsSoBTotalCalc)
      .map((el) => el.categoryTranslation)
      .join(', ')

    return [{ key: '{categories}', value: sobCalcCategories }]
  }, [props.allDataSets])

  const isShowSoB = useMemo(() => {
    const isSobCategoriesExist = !!props.allDataSets.filter((el) => el.IsSoBTotalCalc).length
    const isNoSplitPlans = !isUseContractuals || splitType === 'Total'

    return (
      !isHideSoB &&
      !props.composedReportMode &&
      isNoSplitPlans &&
      ((!!partnerInfo?.IsGlobal && !!props.globalCategory?.IsSoBAvailable) ||
        (!partnerInfo?.IsGlobal && isSobCategoriesExist))
    )
  }, [
    isHideSoB,
    isUseContractuals,
    partnerInfo?.IsGlobal,
    props.allDataSets,
    props.composedReportMode,
    props.globalCategory?.IsSoBAvailable,
    splitType,
  ])

  const isShowGlobalSoB = useMemo(() => {
    return (
      !props.composedReportMode && !!partnerInfo?.IsGlobal && !!props.globalCategory?.IsSoBAvailable
    )
  }, [partnerInfo?.IsGlobal, props.composedReportMode, props.globalCategory?.IsSoBAvailable])

  const commonExtraRowsProps = useMemo(() => {
    return {
      isDistributorPlanner: props.isDistributorPlanner,
      isShowPartnerCount,

      isShowYoYActualsVsActuals,
      isShowYoYActualsVsPlans,
      isShowYoYPlansVsPlans,

      isShowPlanAchievement,

      isShowQoQActualsVsActuals,
      isShowQoQActualsVsPlans,
      isShowQoQPlansVsPlans,
    }
  }, [
    props.isDistributorPlanner,
    isShowPartnerCount,
    isShowPlanAchievement,
    isShowQoQActualsVsActuals,
    isShowQoQActualsVsPlans,
    isShowQoQPlansVsPlans,
    isShowYoYActualsVsActuals,
    isShowYoYActualsVsPlans,
    isShowYoYPlansVsPlans,
  ])

  /** -------------------- Methods ----------------------- */

  const generateTranslationKey = useCallback((option: string) => {
    const suffix = option.trim().replace(/\s/g, '').toLowerCase()

    return `${PLAN_SECTION_KEY}-displaymode-${suffix}`
  }, [])

  const isCategoryNA = () => {
    let isNotApplicable = !isAlwaysShowCategoryNA
    if (partnerInfo) {
      const sectionSetting = sectionSettings[catNAField]
      if (sectionSetting == undefined || sectionSetting == null) {
        // sectionSettings Not defined
        if (hideIfEmpty && !isDataPresent) {
          return true
        } else {
          // Not hideIfEmpty or dataPresent > use Original behave
          isNotApplicable = isNotApplicable && isSectionSettingOn(sectionSetting)
        }
      } else {
        // sectionSettings defined > use Original behave
        isNotApplicable = isNotApplicable && isSectionSettingOn(sectionSetting)
      }
    }

    return isNotApplicable && !props.composedReportMode
  }

  const isOn = (key: string, type: 'quarter' | 'sob' | 'units', defaultValue: boolean = false) => {
    let settingValue = defaultValue

    if (partnerInfo) {
      const field = getNAField(key, type)
      const val = sectionSettings[field]
      if (val !== undefined && val !== null) {
        settingValue = isSectionSettingOn(val)
      }
    }

    return settingValue
  }

  const isOnlyQuarterlyPlanning =
    !!props.dataSetInfo.IsOnlyQuarterlyPlanning && jbpSettings.currentFYear === fyear
  const isPlanningSmbQuarterly =
    !isYearlyPlanning &&
    (isOnlyQuarterlyPlanning || isOn(currentCat, 'quarter', IS_QUARTER_DEFAULT && !isRetailer))
  const formatPlanningSmbMoney = useCallback(
    (value: number) =>
      formatMoneyInteger(
        value,
        getFractionDigits(props.roundingValue),
        getDivisor(props.roundingValue)
      ),
    [props.roundingValue]
  )

  const renderSmbVsEuSplitRows = ([smbValue, euValue]: [number, number], rounding: string) => (
    <div className={styles.itemActual}>{smbVsEuWrapper(smbValue, euValue, rounding)}</div>
  )

  const preparePlanningData = () => {
    setIsDataLoaded(false)

    const isComposedReport = !!props.composedReportMode

    let filteredData = props.planningData.filter(
      (el) =>
        (el.ProgramTypes === buttonCurrType || isComposedReport) &&
        (!el.SubCategory || el.SubCategory === currentCat) &&
        !!el.IsTotal &&
        (!props.globalCategory || el.GlobalCategory === globalCategoryKey)
    )

    if (!isComposedReport) {
      // Show distributor's group data for Regional Distributor:
      // Get data from all Partner in group, Except Regional Distributor
      if (isRegionalDistributor && !props.isRegionalPlanning) {
        filteredData = filteredData.filter((el) => el.PartnerId !== partnerId)
      } else {
        filteredData = filteredData.filter((el) => el.PartnerId === partnerId)
      }
    }

    const rawTargets = filteredData.filter(
      (el) =>
        !!el.IsTarget && (!isComposedReport || !el.IsNoSplit) && !el.IsRollUp && !el.IsPartnerCount
    )
    const rawActuals = filteredData.filter(
      (el) =>
        !!el.IsActuals && (!isComposedReport || !el.IsNoSplit) && !el.IsRollUp && !el.IsPartnerCount
    )
    const rawTargetsRollup = filteredData.filter((el) => !!el.IsTarget && !!el.IsRollUp)
    const rawActualsRollup = filteredData.filter((el) => !!el.IsActuals && !!el.IsRollUp)
    const rawActualsPartnerCount = filteredData.filter(
      (el) => !!el.IsPartnerCount && !!el.IsActuals
    )

    const futureYears = isShowFutureYears ? 2 : 0
    const lastFYear = fyear + futureYears

    const commonParams: IPreparePlanningItems = {
      planningSettings: props.formats,
      source: rawTargets,
      partnerId: partnerId,
      fyear: fyear,
      lastFYear: lastFYear,
      isActuals: 0,
      isDirect: 0,
      isTarget: 1,
      isQuota: 0,
      isComposedReport: isComposedReport,
      isNoSplit: 0,
      isRollUp: 0,
      isTotal: 1,
      isPartnerCount: 0,
      globalCategory: globalCategoryKey,
      smbFraction: SMBFractionTypes.Total,
    }

    const targetsItems = preparePlanningItems(commonParams)
    const actualsItems = preparePlanningItems({
      ...commonParams,
      source: rawActuals,
      isTarget: 0,
      isActuals: 1,
    })
    const targetsRollupItems = preparePlanningItems({
      ...commonParams,
      source: rawTargetsRollup,
      isRollUp: 1,
    })
    const actualsRollupItems = preparePlanningItems({
      ...commonParams,
      source: rawActualsRollup,
      isTarget: 0,
      isActuals: 1,
      isRollUp: 1,
    })
    const actualsPartnerCountItems = preparePlanningItems({
      ...commonParams,
      source: rawActualsPartnerCount,
      isTarget: 0,
      isActuals: 1,
      isPartnerCount: 1,
    })
    // Prepare SMB vs EU split data: same source rows, narrowed by the fraction
    const actualsSmbItems = preparePlanningItems({
      ...commonParams,
      source: rawActuals,
      isTarget: 0,
      isActuals: 1,
      smbFraction: SMBFractionTypes.SMB,
    })
    const actualsEuItems = preparePlanningItems({
      ...commonParams,
      source: rawActuals,
      isTarget: 0,
      isActuals: 1,
      smbFraction: SMBFractionTypes.EU,
    })
    const targetsSmbItems = preparePlanningItems({
      ...commonParams,
      source: rawTargets,
      smbFraction: SMBFractionTypes.SMB,
    })
    const targetsEuItems = preparePlanningItems({
      ...commonParams,
      source: rawTargets,
      smbFraction: SMBFractionTypes.EU,
    })

    setTargetsItems(targetsItems)
    setActualsItems(actualsItems)
    setTargetsRollupItems(targetsRollupItems)
    setActualsRollupItems(actualsRollupItems)
    setActualsPartnerCountItems(actualsPartnerCountItems)
    setActualsSmbItems(actualsSmbItems)
    setActualsEuItems(actualsEuItems)
    setTargetsSmbItems(targetsSmbItems)
    setTargetsEuItems(targetsEuItems)

    if (rawTargets.length) {
      const isCollapsedCategory = getIsCollapsedCategory(targetsItems, fyear)
      setIsCollapsed(isCollapsedCategory)
    }

    // Begin: calculate hideIfEmpty, dataNotEmpty
    let hideEmptyData: boolean = false
    if (props.formats) {
      props.formats.map((el) => {
        if (!!el.HideIfEmpty && !hideIfEmpty) {
          setHideIfEmpty(true)
          hideEmptyData = true
        }
      })
    }

    if (hideEmptyData) {
      let isDataPresent = false
      targetsItems.map((el) => {
        if (el.IsDataPresent) {
          isDataPresent = true
        }
      })
      setIsDataPresent(isDataPresent)
    }
    // End: calculate hideIfEmpty, dataNotEmpty

    setIsDataLoaded(true)
  }

  const getComment = (settingsIndex: number, fy: number, fq: number): string => {
    const items = targetsItems

    if (settingsIndex < props.formats.length) {
      const setting = props.formats[settingsIndex]
      return getCommentInternal(setting, items, fy, fq)
    } else {
      return ''
    }
  }

  const getPopupComment = (settingsIndex: number): string => {
    return settingsIndex < props.formats.length ? props.formats[settingsIndex].PopupComment : ''
  }

  const getTotalValue = (
    settingsIndex: number,
    type: 'actuals' | 'targets',
    fy: number,
    fq: number,
    isUnits: boolean,
    isContractual: boolean,
    isSoB: boolean,
    isRollUp: boolean,
    isDirect: boolean,
    smbFraction: SMBFractionTypes = SMBFractionTypes.Total
  ): number => {
    let items = targetsItems
    if (type === 'actuals') {
      items = actualsItems
    }
    if (isRollUp) {
      if (type === 'actuals') {
        items = actualsRollupItems
      } else {
        items = targetsRollupItems
      }
    }
    // SMB vs EU split has its own datasets, only for plain Actuals / Targets
    if (smbFraction !== SMBFractionTypes.Total && !isRollUp) {
      const isSmb = smbFraction === SMBFractionTypes.SMB
      if (type === 'actuals') {
        items = isSmb ? actualsSmbItems : actualsEuItems
      } else {
        items = isSmb ? targetsSmbItems : targetsEuItems
      }
    }

    if (settingsIndex < props.formats.length) {
      const setting = props.formats[settingsIndex]
      return getTotalValueInternal(
        setting,
        items,
        fy,
        fq,
        currentValueType,
        isUnits,
        false,
        currency,
        isContractual,
        isSoB,
        isDirect
      )
    } else {
      return 0
    }
  }

  const getCalcActuals = (
    settingsIndex: number,
    fy: number,
    isUnits: boolean,
    isContractual: boolean,
    isSoB: boolean,
    isDirect: boolean,
    smbFraction: SMBFractionTypes = SMBFractionTypes.Total
  ): number => {
    const actualQ1 = getTotalValue(
      settingsIndex,
      'actuals',
      fy,
      1,
      isUnits,
      isContractual,
      isSoB,
      false,
      isDirect,
      smbFraction
    )
    const actualQ2 = getTotalValue(
      settingsIndex,
      'actuals',
      fy,
      2,
      isUnits,
      isContractual,
      isSoB,
      false,
      isDirect,
      smbFraction
    )
    const actualQ3 = getTotalValue(
      settingsIndex,
      'actuals',
      fy,
      3,
      isUnits,
      isContractual,
      isSoB,
      false,
      isDirect,
      smbFraction
    )
    const targetQ4 = getTotalValue(
      settingsIndex,
      'targets',
      fy,
      4,
      isUnits,
      isContractual,
      isSoB,
      false,
      isDirect,
      smbFraction
    )

    return (actualQ1 || 0) + (actualQ2 || 0) + (actualQ3 || 0) + (targetQ4 || 0)
  }

  // SMB vs EU split: both values come from their own sp rows, the Total row is not
  // their exact sum, so each fraction has to be read separately
  const getSmbEuTotalValues = (
    settingsIndex: number,
    type: 'actuals' | 'targets',
    fy: number,
    fq: number,
    isUnits: boolean,
    isContractual: boolean,
    isSoB: boolean,
    isRollUp: boolean
  ): [number, number] => [
    getTotalValue(
      settingsIndex,
      type,
      fy,
      fq,
      isUnits,
      isContractual,
      isSoB,
      isRollUp,
      false,
      SMBFractionTypes.SMB
    ),
    getTotalValue(
      settingsIndex,
      type,
      fy,
      fq,
      isUnits,
      isContractual,
      isSoB,
      isRollUp,
      false,
      SMBFractionTypes.EU
    ),
  ]

  const getCalcActualsSmbEu = (
    settingsIndex: number,
    fy: number,
    isUnits: boolean,
    isContractual: boolean,
    isSoB: boolean
  ): [number, number] => [
    getCalcActuals(settingsIndex, fy, isUnits, isContractual, isSoB, false, SMBFractionTypes.SMB),
    getCalcActuals(settingsIndex, fy, isUnits, isContractual, isSoB, false, SMBFractionTypes.EU),
  ]

  const getTotalValueInternal = (
    setting: IFormActualsTargetsSettings,
    items: IJBPPlanningItem[],
    fy: number,
    fq: number,
    actualsType: ActualsValueTypes,
    isUnits: boolean,
    isShipment: boolean,
    currency: string,
    isContractual: boolean,
    isSoB: boolean,
    isDirect: boolean
  ): number => {
    let result = 0

    // Other sales types: SellIn, SellOut, SellTo
    const contrStr = isContractual ? 'Contractual' : ''
    let saleStr = isShipment ? 'Shipment' : 'SellThru'
    if (isContractual) {
      saleStr = ''
    }
    const unitStr = isUnits ? 'Units' : 'Sum'
    let currStr = !currency || currency === 'USD' ? 'USD' : 'LC'
    let actualTypeStr = actualsType
    if (isUnits) {
      currStr = ''
      if (actualsType === ActualsValueTypes.Net) {
        actualTypeStr = ActualsValueTypes.None
      }
    }
    const directStr = isDirect && !isSoB ? 'Direct' : ''
    let fieldName = `${contrStr}${saleStr}${unitStr}${currStr}${actualTypeStr}${directStr}`
    if (isSoB) {
      fieldName = 'SoB'
    }

    // Items filtered on Top level by: PartnerId, IsActuals, IsTarget, IsQuota
    const sourceData = items.filter(
      (el) =>
        (el.ProgramTypes === setting.ProgramType || props.fullTotalMode) &&
        // For all categories
        //el.Category === setting.Category &&
        // Overall only
        (!el.SubCategory || el.SubCategory === OVERALL_CATEGORY) &&
        el.FYear === fy &&
        el.FQuarter === fq
    )

    // Sum variant
    //sourceData.forEach((el) => { result += Number(el[fieldName] || 0) })

    // Without Sum variant
    if (sourceData.length >= 1) {
      const value = sourceData[0][fieldName]
      result = value === undefined ? null : value
    } else {
      // error in data
    }

    return result
  }

  const getCommentInternal = (
    setting: IFormActualsTargetsSettings,
    items: IJBPPlanningItem[],
    fy: number,
    fq: number
  ): string => {
    let result = ''

    // Items filtered on Top level by: PartnerId, IsActuals, IsTarget
    const sourceData = items.filter(
      (el) =>
        el.ProgramTypes === setting.ProgramType &&
        el.Category === setting.Category &&
        el.SubCategory === setting.SubCategory &&
        el.FYear === fy &&
        el.FQuarter === fq
    )
    if (sourceData.length >= 1) {
      result = sourceData[0].Comment ?? ''
    } else {
      // error in data
    }

    return result
  }

  /** -------------------- User actions handlers ----------------------- */

  /** -------------------- Render methods ----------------------- */

  const fieldTotalWrapper = (
    fieldValue: number,
    year: number,
    fieldType: 'targets' | 'actuals' | 'quota' = 'targets',
    rounding: string //= ''
  ) => {
    const divisor = getDivisor(rounding)
    const roundingFractionDigits = getFractionDigits(rounding)
    const fractionDigits = roundingFractionDigits ? roundingFractionDigits : 0

    let formattedValue = formatMoneyInteger(fieldValue, fractionDigits, divisor)
    if (fieldValue === null) {
      formattedValue = '-'
    }

    let isUseColor = false
    let color = targetsColor
    switch (fieldType) {
      case 'actuals': {
        color = actualsColor
        isUseColor = true
        break
      }
      case 'quota': {
        color = quotaColor
        isUseColor = true
        break
      }
      case 'targets':
      default: {
        // Not use custom Color
        break
      }
    }

    if (!isRetailer) {
      return !isUseColor ? formattedValue : <div style={{ color: color }}>{formattedValue}</div>
    } else {
      return (
        <>{!isUseColor ? formattedValue : <div style={{ color: color }}>{formattedValue}</div>}</>
      )
    }
  }

  const renderQ = (
    idx: number,
    q: number,
    isEditAllowed: boolean,
    isUnits: boolean,
    isRollUp: boolean,
    mod: 'prevPrev' | 'prev' | 'calc' | 'curr' | 'futr'
  ) => {
    const isPrevPrevMod = mod === 'prevPrev'
    //const isPrevMod = mod === 'prev'
    const isPrevMod = mod === 'prev' || mod === 'calc'

    const isCalc = mod === 'calc'
    const isCurr = mod !== 'calc' // mod === 'curr' || mod === 'futr'

    const isCurrFY = mod === 'curr' // Only for visualizations
    const isFuture = mod === 'futr' // Only for visualizations

    const originalQ = q
    const isPrevYear = q < 0
    q = Math.abs(q)

    const isPlanningByFY = q >= PLANNING_BY_FY_FQ

    let futureYears = 0
    if (q > PLANNING_BY_FY_FQ) {
      futureYears = q - PLANNING_BY_FY_FQ
      q = PLANNING_BY_FY_FQ
    }
    const year = fyear - (isPrevPrevMod ? 2 : 0) - (isPrevMod ? 1 : 0) + futureYears

    const isUseCalcActualsForYoY = isPlanningByFY && !isPrevYear && !isFuture && isShowCalcActuals

    const isSoB = false
    const isContr = false

    //const isShowPrevFY = !isPlanningByFY || (isShowPrePreYear && isPrevYear)
    //const isShowPrevCalc = isPlanningByFY && isPrevYear
    //const isShowPrevCalcActuals = isShowPrevCalc && isShowCalcActuals
    const rounding = isUnits ? '' : props.roundingValue

    let prevFq = q - 1
    let prevFqFy = year
    if (q >= PLANNING_BY_FY_FQ) {
      // Planning by FY
      prevFq = q
      prevFqFy = year - 1
    } else if (q === 1) {
      prevFq = 4
      prevFqFy = year - 1
    }
    // Check: isRollUp always = false for Actuals?
    const preFqTargets = getTotalValue(
      idx,
      'targets',
      prevFqFy,
      prevFq,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )
    const preFqActuals = getTotalValue(
      idx,
      'actuals',
      prevFqFy,
      prevFq,
      isUnits,
      isContr,
      isSoB,
      false,
      false
    )

    const preActuals = getTotalValue(
      idx,
      'actuals',
      year - 1,
      q,
      isUnits,
      isContr,
      isSoB,
      false,
      false
    )
    const currActuals = getTotalValue(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      isContr,
      isSoB,
      false,
      false
    )
    const currActualsDirect = getTotalValue(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      isContr,
      isSoB,
      false,
      true
    )
    const preTargets = getTotalValue(
      idx,
      'targets',
      year - 1,
      q,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )
    const currTargets = getTotalValue(
      idx,
      'targets',
      year,
      q,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )
    const comment = getComment(idx, year, q)

    const calActualsFy = isUseCalcActualsForYoY ? year - 1 : year
    const preActualsCalc = getCalcActuals(idx, calActualsFy, isUnits, isContr, isSoB, false)
    const preActualsCalcDirect = getCalcActuals(idx, calActualsFy, isUnits, isContr, isSoB, true)

    // SMB vs EU split
    const preActualsCalcSmbEu = getCalcActualsSmbEu(idx, calActualsFy, isUnits, isContr, isSoB)
    const currActualsSmbEu = getSmbEuTotalValues(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      isContr,
      isSoB,
      false
    )
    const currTargetsSmbEu = getSmbEuTotalValues(
      idx,
      'targets',
      year,
      q,
      isUnits,
      isContr,
      isSoB,
      isRollUp
    )

    const preTargetsForYoY = preTargets
    const preActualsForYoY = isUseCalcActualsForYoY || mod === 'calc' ? preActualsCalc : preActuals

    const sTotal = ''
    const fsProps: IGetFinSobValueProps = {
      targetsItems,
      targetsNoSplitItems: [],
      actualsItems,
      actualsNoSplitItems: [],
      quotaItems: [],
      targetsRollupItems: targetsRollupItems,
      actualsRollupItems: actualsRollupItems,
      actualsPartnerCountItems,

      formats: props.formats,
      partnerId,
      actualsType: currentValueType,
      currency,
      isCollapsed,
      isShowOnlyCompletedPlans,
      isShowComposedSoB: false,
      isRecalcPlans: false,
      isComposedReportMode: props.composedReportMode,
      isQuarterlyBusinessReviewMode: props.isQuarterlyBusinessReviewMode,

      settingsIndex: idx,
      type: 'targets',
      fy: year,
      fq: q,
      isUnits,
      sType: sTotal,
      isSoB,
      isNoSplit: false,
      isRollUp,
      isDirect: false,

      fieldName: '',
    }

    const partnerCount = getFinSobValue({ ...fsProps, type: 'partnerCount' })
    const partnerCountPercent = partnerCount ? 100 : 0
    const partnerCountPreFy = getFinSobValue({ ...fsProps, type: 'partnerCount' })
    const partnerCountPreFq = getFinSobValue({
      ...fsProps,
      type: 'partnerCount',
      fy: prevFqFy,
      fq: prevFq,
    })

    const extraRowsProps = {
      ...commonExtraRowsProps,
      isPlanningByFY,

      partnerCount,
      partnerCountPercent,
      partnerCountYoY: calcDiff2(partnerCountPreFy, partnerCount),
      partnerCountQoQ: calcDiff2(partnerCountPreFq, partnerCount),
      isShowYoYPartnersVsPartners,
      isShowQoQPartnersVsPartners,

      yoyPlans: calcPercentage(preTargetsForYoY, currTargets),
      yoyTargets: calcPercentage(preActualsForYoY, currTargets),
      yoyActuals: calcPercentage(preActualsForYoY, currActuals),

      yoyTargetsAch: calcPercentage2(currActuals, currTargets),

      qoqPlans: calcPercentage(preFqTargets, currTargets),
      qoqTargets: calcPercentage(preFqActuals, currTargets),
      qoqActuals: calcPercentage(preFqActuals, currActuals),
    }

    const isQuarterPlanning = isOn(currentCat, 'quarter', IS_QUARTER_DEFAULT)
    const isActiveColumns =
      (isQuarterPlanning && !isPlanningByFY) || (!isQuarterPlanning && isPlanningByFY)

    const isEditAllowedForCollapsed = isEditAllowed && isCollapsed
    const isEditByQuarter = isEditAllowedForCollapsed && isActiveColumns && false

    const isHidePrevTargets = !isShowPrevTargets && !IS_NEW_LNF
    const isHideCurrTargets = isHidePrevTargets && isPrevYear

    const isShowActualsCell = props.showActuals
    const isStripCol = !isActiveColumns && !isRollUp

    const isShowSeparator = isPlanningByFY
    const isShowSeparatorThin = !isPlanningByFY && q !== 1

    const key = `jbp-planning-cells-q-${currentCat}-${idx}-${originalQ}-${isUnits}-${isRollUp}-${mod}`

    return (
      <Fragment key={key}>
        {/* Prev FY (Calc) */}

        {isCalc && isShowCalcActuals && (
          <td
            style={{ backgroundColor: getPlanningPeriodColumnColor(isPlanningByFY, isFuture) }}
            className={cn({
              [styles.colStrip]: isStripCol,
              [styles.colStripCurrent]: isCurrFY,
              [styles.colSeparator]: isPlanningByFY,
              [styles.rollUp]: isRollUp,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                <div className={styles.itemActual}>
                  {isShowActualsCell &&
                    fieldTotalWrapper(preActualsCalc, year - 1, 'actuals', rounding)}
                </div>
                {isShowActualsSplit && isShowActualsCell && (
                  <div className={styles.itemActual}>
                    {actualsWrapper(
                      preActualsCalc,
                      preActualsCalcDirect,
                      year - 1,
                      isRetailer,
                      marketShareData,
                      false,
                      0,
                      rounding
                    )}
                  </div>
                )}
                {isShowSmbVsEuSplit &&
                  isShowActualsCell &&
                  renderSmbVsEuSplitRows(preActualsCalcSmbEu, rounding)}
                <div className={cn(styles.itemTarget)}>
                  {isHideCurrTargets ? (
                    <>&nbsp;</>
                  ) : (
                    <>{fieldTotalWrapper(currTargets, year, 'targets', rounding)}</>
                  )}
                </div>
                {isShowSmbVsEuSplit && renderSmbVsEuSplitRows(currTargetsSmbEu, rounding)}
                {/* PartnerCount, YoY, QoQ */}
                <ExtraFinRowsTotal {...extraRowsProps} />
              </div>
            </div>
          </td>
        )}

        {/* Current FY */}

        {isCurr && (
          <td
            style={{ backgroundColor: getPlanningPeriodColumnColor(isPlanningByFY, isFuture) }}
            className={cn(styles.colSeparator, {
              [styles.colStrip]: isStripCol,
              [styles.colStripCurrent]: isCurrFY,
              [styles.colSeparator]: isShowSeparator,
              [styles.colSeparatorThin]: isShowSeparatorThin,
              [styles.rollUp]: isRollUp,
            })}
          >
            <div
              className={cn(styles.itemWrapper, {
                [styles.itemOverall]: !isShowActualsCell && !isEditByQuarter,
              })}
            >
              <div className={styles.itemValues}>
                <div className={styles.itemActual}>
                  {isShowActualsCell && fieldTotalWrapper(currActuals, year, 'actuals', rounding)}
                </div>
                {isShowActualsSplit && isShowActualsCell && (
                  <div className={styles.itemActual}>
                    {actualsWrapper(
                      currActuals,
                      currActualsDirect,
                      year,
                      isRetailer,
                      marketShareData,
                      false,
                      0,
                      rounding
                    )}
                  </div>
                )}
                {isShowSmbVsEuSplit &&
                  isShowActualsCell &&
                  renderSmbVsEuSplitRows(currActualsSmbEu, rounding)}
                <div className={styles.itemTarget}>
                  {!isEditByQuarter && isShowComments && <CommentIcon tooltipText={comment} />}
                  {isHideCurrTargets ? (
                    <>&nbsp;</>
                  ) : (
                    <>{fieldTotalWrapper(currTargets, year, undefined, rounding)}</>
                  )}
                </div>
                {isShowSmbVsEuSplit && renderSmbVsEuSplitRows(currTargetsSmbEu, rounding)}
                {/* PartnerCount, YoY, QoQ */}
                <ExtraFinRowsTotal {...extraRowsProps} />
              </div>
            </div>
          </td>
        )}

        {/*<td className={cn({ [styles.colStrip]: isStripCol, [styles.rollUp]: isRollUp })}>
          <div className={styles.itemWrapper}>
            <div className={cn(styles.itemIcon, { [styles.itemEditIcon]: isEditByQuarter })}>
              {isShowActualsCell && <GrowField percent={yoyActuals} isPaleColor={true} />}
              {isHideCurrTargets ? <span>&nbsp;</span> : <GrowField percent={yoyTargets} />}
            </div>
          </div>
        </td>*/}
      </Fragment>
    )
  }

  const renderQContr = (
    idx: number,
    q: number,
    isUnits: boolean,
    ctType: TCTType,
    mod: 'prevPrev' | 'prev' | 'calc' | 'curr' | 'futr'
  ) => {
    const isPrevPrevMod = mod === 'prevPrev'
    //const isPrevMod = mod === 'prev'
    const isPrevMod = mod === 'prev' || mod === 'calc'

    const isCalc = mod === 'calc'
    const isCurr = mod !== 'calc' // mod === 'curr' || mod === 'futr'

    //const isCurrFY = mod === 'curr' // Only for visualizations
    const isFuture = mod === 'futr' // Only for visualizations

    const originalQ = q
    const isPrevYear = q < 0
    q = Math.abs(q)

    const isPlanningByFY = q >= PLANNING_BY_FY_FQ

    let futureYears = 0
    if (q > PLANNING_BY_FY_FQ) {
      futureYears = q - PLANNING_BY_FY_FQ
      q = PLANNING_BY_FY_FQ
    }
    const year = fyear - (isPrevPrevMod ? 2 : 0) - (isPrevMod ? 1 : 0) + futureYears

    const isUseCalcActualsForYoY = isPlanningByFY && !isPrevYear && !isFuture && isShowCalcActuals

    const isSoB = false
    const isRollUp = false

    // const isShowPrevFY = !isPlanningByFY || (isShowPrePreYear && isPrevYear)
    // const isShowPrevCalc = isPlanningByFY && isPrevYear
    // const isShowPrevCalcActuals = isShowPrevCalc && isShowCalcActuals
    const rounding = isUnits ? '' : props.roundingValue

    let prevFq = q - 1
    let prevFqFy = year
    if (q >= PLANNING_BY_FY_FQ) {
      // Planning by FY
      prevFq = q
      prevFqFy = year - 1
    } else if (q === 1) {
      prevFq = 4
      prevFqFy = year - 1
    }

    const preFqActualsTotal = getTotalValue(
      idx,
      'actuals',
      prevFqFy,
      prevFq,
      isUnits,
      false,
      isSoB,
      false,
      false
    )
    const preFqTargetsTotal = getTotalValue(
      idx,
      'targets',
      prevFqFy,
      prevFq,
      isUnits,
      false,
      isSoB,
      false,
      false
    )
    const preFqActualsContr = getTotalValue(
      idx,
      'actuals',
      prevFqFy,
      prevFq,
      isUnits,
      true,
      isSoB,
      false,
      false
    )
    const preFqTargetsContr = getTotalValue(
      idx,
      'targets',
      prevFqFy,
      prevFq,
      isUnits,
      true,
      isSoB,
      false,
      false
    )

    const preActualsTotal = getTotalValue(
      idx,
      'actuals',
      year - 1,
      q,
      isUnits,
      false,
      isSoB,
      false,
      false
    )
    const currActualsTotal = getTotalValue(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      false,
      isSoB,
      false,
      false
    )
    const preTargetsTotal = getTotalValue(
      idx,
      'targets',
      year - 1,
      q,
      isUnits,
      false,
      isSoB,
      false,
      false
    )
    const currTargetsTotal = getTotalValue(
      idx,
      'targets',
      year,
      q,
      isUnits,
      false,
      isSoB,
      false,
      false
    )

    const preActualsContr = getTotalValue(
      idx,
      'actuals',
      year - 1,
      q,
      isUnits,
      true,
      isSoB,
      false,
      false
    )
    const currActualsContr = getTotalValue(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      true,
      isSoB,
      false,
      false
    )
    const preTargetsContr = getTotalValue(
      idx,
      'targets',
      year - 1,
      q,
      isUnits,
      true,
      isSoB,
      false,
      false
    )
    const currTargetsContr = getTotalValue(
      idx,
      'targets',
      year,
      q,
      isUnits,
      true,
      isSoB,
      false,
      false
    )

    const calActualsFy = isUseCalcActualsForYoY ? year - 1 : year
    const preActualsTotalCalc = getCalcActuals(idx, calActualsFy, isUnits, false, isSoB, false) // Total
    const preActualsContrCalc = getCalcActuals(idx, calActualsFy, isUnits, true, isSoB, false) // Contractual
    const preActualsTranCalc = Number(preActualsTotalCalc || 0) - Number(preActualsContrCalc || 0)

    const currActualsDirectTotal = getTotalValue(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      false,
      isSoB,
      false,
      true
    )
    const currActualsDirectContr = getTotalValue(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      true,
      isSoB,
      false,
      true
    )
    const currActualsDirectTran =
      Number(currActualsDirectTotal || 0) - Number(currActualsDirectContr || 0)
    const preActualsDirectTotalCalc = getCalcActuals(idx, calActualsFy, isUnits, false, isSoB, true)
    const preActualsDirectContrCalc = getCalcActuals(idx, calActualsFy, isUnits, true, isSoB, true)
    const preActualsDirectTranCalc =
      Number(preActualsDirectTotalCalc || 0) - Number(preActualsDirectContrCalc || 0)

    const preActualsTran = Number(preActualsTotal || 0) - Number(preActualsContr || 0)
    const currActualsTran = Number(currActualsTotal || 0) - Number(currActualsContr || 0)
    const preTargetsTran = Number(preTargetsTotal || 0) - Number(preTargetsContr || 0)
    const currTargetsTran = Number(currTargetsTotal || 0) - Number(currTargetsContr || 0)
    const preFqActualsTran = Number(preFqActualsTotal || 0) - Number(preFqActualsContr || 0)
    const preFqTargetsTran = Number(preFqTargetsTotal || 0) - Number(preFqTargetsContr || 0)

    // SMB vs EU split
    const preActualsCalcTotalSmbEu = getCalcActualsSmbEu(idx, calActualsFy, isUnits, false, isSoB)
    const preActualsCalcContrSmbEu = getCalcActualsSmbEu(idx, calActualsFy, isUnits, true, isSoB)
    const preActualsCalcTranSmbEu = getSmbEuDiff(preActualsCalcTotalSmbEu, preActualsCalcContrSmbEu)

    const currActualsTotalSmbEu = getSmbEuTotalValues(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      false,
      isSoB,
      false
    )
    const currActualsContrSmbEu = getSmbEuTotalValues(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      true,
      isSoB,
      false
    )
    const currActualsTranSmbEu = getSmbEuDiff(currActualsTotalSmbEu, currActualsContrSmbEu)

    const currTargetsTotalSmbEu = getSmbEuTotalValues(
      idx,
      'targets',
      year,
      q,
      isUnits,
      false,
      isSoB,
      false
    )
    const currTargetsContrSmbEu = getSmbEuTotalValues(
      idx,
      'targets',
      year,
      q,
      isUnits,
      true,
      isSoB,
      false
    )
    const currTargetsTranSmbEu = getSmbEuDiff(currTargetsTotalSmbEu, currTargetsContrSmbEu)

    const yoyActualsTran = calcPercentage(preActualsTran, currActualsTran)
    const yoyActualsContr = calcPercentage(preActualsContr, currActualsContr)
    const yoyTargetsTran = calcPercentage(preActualsTran, currTargetsTran)
    const yoyTargetsContr = calcPercentage(preActualsContr, currTargetsContr)

    const yoyPlansContr = calcPercentage(preTargetsContr, currTargetsContr)
    const yoyPlansTran = calcPercentage(preTargetsTran, currTargetsTran)
    //const yoyPlansContr = calcPercentage(preTargetsContrForYoY, currTargetsContr)
    //const yoyPlansTran = calcPercentage(preTargetsTranForYoY, currTargetsTran)
    //const yoyActualsContr = calcPercentage(preActualsContrForYoY, currActualsContr)
    //const yoyActualsTran = calcPercentage(preActualsTranForYoY, currActualsTran)
    //const yoyTargetsContr = calcPercentage(preActualsContrForYoY, currTargetsContr)
    //const yoyTargetsTran = calcPercentage(preActualsTranForYoY, currTargetsTran)

    const yoyTargetsContrAch = calcPercentage2(currActualsContr, currTargetsContr)
    const yoyTargetsTranAch = calcPercentage2(currActualsTran, currTargetsTran)

    const qoqPlansContr = calcPercentage(preFqTargetsContr, currTargetsContr)
    const qoqPlansTran = calcPercentage(preFqTargetsTran, currTargetsTran)
    const qoqActualsContr = calcPercentage(preFqActualsContr, currActualsContr)
    const qoqActualsTran = calcPercentage(preFqActualsTran, currActualsTran)
    const qoqTargetsContr = calcPercentage(preFqActualsContr, currTargetsContr)
    const qoqTargetsTran = calcPercentage(preFqActualsTran, currTargetsTran)

    const isOverall = true

    const isQuarterPlanning = isOn(currentCat, 'quarter', IS_QUARTER_DEFAULT)
    const isActiveColumns =
      (isQuarterPlanning && !isPlanningByFY) || (!isQuarterPlanning && isPlanningByFY)
    const isEditCells = false

    const isShowActualsCell = props.showActuals
    const isItemOverall = isOverall && !props.showActuals && !isEditCells

    const isC = ctType === 'c'
    const isT = ctType === 't'

    const isHidePrevTargets = !isShowPrevTargets && !IS_NEW_LNF
    const isHideCurrTargets = isHidePrevTargets && isPrevYear

    const isShowSeparator = isPlanningByFY
    const isShowSeparatorThin = !isPlanningByFY && q !== 1

    const fsProps: IGetFinSobValueProps = {
      targetsItems,
      targetsNoSplitItems: [],
      actualsItems,
      actualsNoSplitItems: [],
      quotaItems: [],
      targetsRollupItems: targetsRollupItems,
      actualsRollupItems: actualsRollupItems,
      actualsPartnerCountItems,

      formats: props.formats,
      partnerId,
      actualsType: currentValueType,
      currency,
      isCollapsed,
      isShowOnlyCompletedPlans,
      isShowComposedSoB: false,
      isRecalcPlans: false,
      isComposedReportMode: props.composedReportMode,
      isQuarterlyBusinessReviewMode: props.isQuarterlyBusinessReviewMode,

      settingsIndex: idx,
      type: 'targets',
      fy: year,
      fq: q,
      isUnits,
      sType: '',
      isSoB,
      isNoSplit: false,
      isRollUp,
      isDirect: false,
      fieldName: '',
    }

    const partnerCountTotal = getFinSobValue({ ...fsProps, type: 'partnerCount' })
    const partnerCountC = getFinSobValue({ ...fsProps, type: 'partnerCount', sType: 'c' })
    const partnerCountT = partnerCountTotal - partnerCountC
    const partnerCount = isC ? partnerCountC : partnerCountT
    const partnerCountPercent = calcPercentage2(partnerCount, partnerCountTotal) ?? 0

    const partnerCountPreFyTotal = getFinSobValue({ ...fsProps, type: 'partnerCount' })
    const partnerCountPreFyC = getFinSobValue({ ...fsProps, type: 'partnerCount', sType: 'c' })
    const partnerCountPreFqTotal = getFinSobValue({
      ...fsProps,
      type: 'partnerCount',
      fy: prevFqFy,
      fq: prevFq,
    })
    const partnerCountPreFqC = getFinSobValue({
      ...fsProps,
      type: 'partnerCount',
      fy: prevFqFy,
      fq: prevFq,
      sType: 'c',
    })
    const partnerCountPreFyT = partnerCountPreFyTotal - partnerCountPreFyC
    const partnerCountPreFqT = partnerCountPreFqTotal - partnerCountPreFqC
    const partnerCountPreFy = isC ? partnerCountPreFyC : partnerCountPreFyT
    const partnerCountPreFq = isC ? partnerCountPreFqC : partnerCountPreFqT

    const extraRowsProps = {
      ...commonExtraRowsProps,
      isPlanningByFY,
      sType: ctType,

      partnerCount,
      partnerCountPercent,
      partnerCountYoY: calcDiff2(partnerCountPreFy, partnerCount),
      partnerCountQoQ: calcDiff2(partnerCountPreFq, partnerCount),
      isShowYoYPartnersVsPartners,
      isShowQoQPartnersVsPartners,

      yoyPlans: isC ? yoyPlansContr : yoyPlansTran,
      yoyTargets: isC ? yoyTargetsContr : yoyTargetsTran,
      yoyActuals: isC ? yoyActualsContr : yoyActualsTran,

      yoyTargetsAch: isC ? yoyTargetsContrAch : yoyTargetsTranAch,

      qoqPlans: isC ? qoqPlansContr : qoqPlansTran,
      qoqTargets: isC ? qoqTargetsContr : qoqTargetsTran,
      qoqActuals: isC ? qoqActualsContr : qoqActualsTran,
    }

    const key = `jbp-planning-cells-q-contr-${currentCat}-${idx}-${originalQ}-${isUnits}-${isRollUp}-${mod}`

    return (
      <Fragment key={key}>
        {/* Prev FY (Calc) */}

        {isCalc && isShowCalcActuals && (
          <td
            style={{ backgroundColor: getPlanningPeriodColumnColor(isPlanningByFY, isFuture) }}
            className={cn({
              [styles.colStrip]: !isActiveColumns,
              [styles.colSeparator]: isPlanningByFY,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                {isC && (
                  <>
                    <div className={styles.itemActual}>
                      {fieldTotalWrapper(preActualsContrCalc, year - 1, 'actuals', rounding)}
                    </div>
                    {isShowActualsSplit && (
                      <div className={styles.itemActual}>
                        {actualsWrapper(
                          preActualsContrCalc,
                          preActualsDirectContrCalc,
                          year - 1,
                          isRetailer,
                          marketShareData,
                          false,
                          0,
                          rounding
                        )}
                      </div>
                    )}
                    {isShowSmbVsEuSplit &&
                      renderSmbVsEuSplitRows(preActualsCalcContrSmbEu, rounding)}
                    <div className={cn(styles.itemTarget)}>
                      {isHideCurrTargets ? (
                        <>&nbsp;</>
                      ) : (
                        <>{fieldTotalWrapper(currTargetsContr, year, 'targets', rounding)}</>
                      )}
                    </div>
                    {isShowSmbVsEuSplit && renderSmbVsEuSplitRows(currTargetsContrSmbEu, rounding)}
                  </>
                )}
                {isT && (
                  <>
                    <div className={styles.itemActual}>
                      {fieldTotalWrapper(preActualsTranCalc, year - 1, 'actuals', rounding)}
                    </div>
                    {isShowActualsSplit && (
                      <div className={styles.itemActual}>
                        {actualsWrapper(
                          preActualsTranCalc,
                          preActualsDirectTranCalc,
                          year - 1,
                          isRetailer,
                          marketShareData,
                          false,
                          0,
                          rounding
                        )}
                      </div>
                    )}
                    {isShowSmbVsEuSplit &&
                      renderSmbVsEuSplitRows(preActualsCalcTranSmbEu, rounding)}
                    <div className={cn(styles.itemTarget)}>
                      {isHideCurrTargets ? (
                        <>&nbsp;</>
                      ) : (
                        <>{fieldTotalWrapper(currTargetsTran, year, 'targets', rounding)}</>
                      )}
                    </div>
                    {isShowSmbVsEuSplit && renderSmbVsEuSplitRows(currTargetsTranSmbEu, rounding)}
                  </>
                )}
                {/* PartnerCount, YoY, QoQ */}
                <ExtraConRowsTotal {...extraRowsProps} />
              </div>
            </div>
          </td>
        )}

        {/* Current FY */}

        {isCurr && (
          <td
            style={{ backgroundColor: getPlanningPeriodColumnColor(isPlanningByFY, isFuture) }}
            className={cn(styles.colSeparator, {
              [styles.colStrip]: !isActiveColumns,
              [styles.colSeparator]: isShowSeparator,
              [styles.colSeparatorThin]: isShowSeparatorThin,
            })}
          >
            <div className={cn(styles.itemWrapper, { [styles.itemOverall]: isItemOverall })}>
              <div className={styles.itemValues}>
                {isC && (
                  <>
                    {isShowActualsCell && (
                      <div className={styles.itemActual}>
                        {isShowActualsCell &&
                          fieldTotalWrapper(currActualsContr, year, 'actuals', rounding)}
                      </div>
                    )}
                    {isShowActualsSplit && isShowActualsCell && (
                      <div className={styles.itemActual}>
                        {actualsWrapper(
                          currActualsContr,
                          currActualsDirectContr,
                          year,
                          isRetailer,
                          marketShareData,
                          false,
                          0,
                          rounding
                        )}
                      </div>
                    )}
                    {isShowSmbVsEuSplit &&
                      isShowActualsCell &&
                      renderSmbVsEuSplitRows(currActualsContrSmbEu, rounding)}
                    <div className={styles.itemTarget}>
                      {isHideCurrTargets ? (
                        <>&nbsp;</>
                      ) : (
                        <>{fieldTotalWrapper(currTargetsContr, year, undefined, rounding)}</>
                      )}
                    </div>
                    {isShowSmbVsEuSplit && renderSmbVsEuSplitRows(currTargetsContrSmbEu, rounding)}
                  </>
                )}
                {isT && (
                  <>
                    {isShowActualsCell && (
                      <div className={styles.itemActual}>
                        {isShowActualsCell &&
                          fieldTotalWrapper(currActualsTran, year, 'actuals', rounding)}
                      </div>
                    )}
                    {isShowActualsSplit && isShowActualsCell && (
                      <div className={styles.itemActual}>
                        {actualsWrapper(
                          currActualsTran,
                          currActualsDirectTran,
                          year,
                          isRetailer,
                          marketShareData,
                          false,
                          0,
                          rounding
                        )}
                      </div>
                    )}
                    {isShowSmbVsEuSplit &&
                      isShowActualsCell &&
                      renderSmbVsEuSplitRows(currActualsTranSmbEu, rounding)}
                    <div className={styles.itemTarget}>
                      {isHideCurrTargets ? (
                        <>&nbsp;</>
                      ) : (
                        <>{fieldTotalWrapper(currTargetsTran, year - 1, undefined, rounding)}</>
                      )}
                    </div>
                    {isShowSmbVsEuSplit && renderSmbVsEuSplitRows(currTargetsTranSmbEu, rounding)}
                  </>
                )}
                {/* PartnerCount, YoY, QoQ */}
                <ExtraConRowsTotal {...extraRowsProps} />
              </div>
            </div>
          </td>
        )}

        {/*<td className={cn({ [styles.colStrip]: !isActiveColumns })}>
          <div className={styles.itemWrapper}>
            <div
              className={cn(
                styles.itemIcon,
                { [styles.itemOneIcon]: !isShowActualsCell },
                { [styles.itemEditIcon]: isEditCells }
              )}
            >
              {isC && (
                <>
                  {isShowActualsCell && <GrowField percent={yoyActualsContr} isPaleColor={true} />}
                  <GrowField percent={yoyTargetsContr} />
                </>
              )}
              {isT && (
                <>
                  {isShowActualsCell && <GrowField percent={yoyActualsTran} isPaleColor={true} />}
                  <div className={cn({ [styles.itemTwoIcon]: isEditCells })}>
                    <GrowField percent={yoyTargetsTran} />
                  </div>
                </>
              )}
            </div>
          </div>
        </td>*/}
      </Fragment>
    )
  }

  const renderSoB = (
    idx: number,
    q: number,
    isUnits: boolean,
    isNoPlan: boolean,
    isRollUp: boolean,
    mod: 'prevPrev' | 'prev' | 'calc' | 'curr' | 'futr'
  ) => {
    const isPrevPrevMod = mod === 'prevPrev'
    //const isPrevMod = mod === 'prev'
    const isPrevMod = mod === 'prev' || mod === 'calc'

    const isCalc = mod === 'calc'
    const isCurr = mod !== 'calc' // mod === 'curr' || mod === 'futr'

    const isCurrFY = mod === 'curr' // Only for visualizations
    const isFuture = mod === 'futr' // Only for visualizations

    const originalQ = q
    const isPrevYear = q < 0
    q = Math.abs(q)

    const isPlanningByFY = q >= PLANNING_BY_FY_FQ

    let futureYears = 0
    if (q > PLANNING_BY_FY_FQ) {
      futureYears = q - PLANNING_BY_FY_FQ
      q = PLANNING_BY_FY_FQ
    }
    const year = fyear - (isPrevPrevMod ? 2 : 0) - (isPrevMod ? 1 : 0) + futureYears

    // const isShowPrevFY = !isPlanningByFY || (isShowPrePreYear && isPrevYear)
    // const isShowPrevCalc = isPlanningByFY && isPrevYear
    // const isShowPrevCalcActuals = isShowPrevCalc && isShowCalcActuals

    const isShowActualsCell = props.showActuals

    const isSoB = true
    const isContr = false

    let prevFq = q - 1
    let prevFqFy = year
    if (q >= PLANNING_BY_FY_FQ) {
      // Planning by FY
      prevFq = q
      prevFqFy = year - 1
    } else if (q === 1) {
      prevFq = 4
      prevFqFy = year - 1
    }
    const preFqTargets = getTotalValue(
      idx,
      'targets',
      prevFqFy,
      prevFq,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )
    const preFqActuals = getTotalValue(
      idx,
      'actuals',
      prevFqFy,
      prevFq,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )

    const preActual = getTotalValue(
      idx,
      'actuals',
      year - 1,
      q,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )
    const preTarget = getTotalValue(
      idx,
      'targets',
      year - 1,
      q,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )
    const curActual = getTotalValue(
      idx,
      'actuals',
      year,
      q,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )
    const curTarget = getTotalValue(
      idx,
      'targets',
      year,
      q,
      isUnits,
      isContr,
      isSoB,
      isRollUp,
      false
    )

    // TODO: is CompletenessItem need for RollUp?
    // const preActCompleteness = getCompletenessItem({formats: props.formats, settingsIndex: idx, items: actualsItems, fy: year - 1, isSoB: true})
    // const preTarCompleteness = getCompletenessItem({formats: props.formats, settingsIndex: idx, items: targetsItems, fy: year - 1, isSoB: true})
    const curActCompleteness = getCompletenessItem({
      formats: props.formats,
      settingsIndex: idx,
      items: actualsItems,
      fy: year,
      isSoB: true,
    })
    const curTarCompleteness = getCompletenessItem({
      formats: props.formats,
      settingsIndex: idx,
      items: targetsItems,
      fy: year,
      isSoB: true,
    })

    const isActiveColumns = true

    const setting = props.formats[idx]

    const flags = partnerInfo?.PartnerFlags || ''
    const isMandatoryByFlag = isSubCategoryMandatory(
      flags,
      setting.SOBMandatoryByFlags,
      sectionSettings,
      false
    )
    const isShowMandatory =
      isMandatoryByFlag.isMandatory && !isPrevYear && !isUnits && !props.composedReportMode

    const isShowZero = !IS_SHOW_NULL && isShowMandatory && Number(curTarget || 0) <= 0

    // const isShowPreActCompletenessField = !!props.composedReportMode && !!preActCompleteness.partnersNotCompleted
    // const isShowPreTarCompletenessField = !!props.composedReportMode && !!preTarCompleteness.partnersNotCompleted
    const isShowCurActCompletenessField =
      !!props.composedReportMode && !!curActCompleteness.partnersNotCompleted
    const isShowCurTarCompletenessField =
      !!props.composedReportMode && !!curTarCompleteness.partnersNotCompleted

    const onSoBCompletenessClick = (partnersCompleteness: IPartnersCompleteness) => {}

    const completenessTag = isShowOnlyCompletedPlans ? 'sobAck' : 'sob'

    const isHidePrevTargets = !isShowPrevTargets && !IS_NEW_LNF
    const isHideCurrTargets = isHidePrevTargets && isPrevYear
    const isHideCurrTargetsYoY = isHideCurrTargets || (isNoPlan && !isPrevYear)

    const isShowSeparator = isPlanningByFY
    const isShowSeparatorThin = !isPlanningByFY && q !== 1

    const extraRowsProps = {
      ...commonExtraRowsProps,
      isPlanningByFY,
      isHideCurrTargetsYoY,
      isCalc,

      yoyPlans: calcDiff2(preTarget, curTarget),
      yoyTargets: calcDiff2(preActual, curTarget),
      yoyActuals: calcDiff2(preActual, curActual),

      qoqPlans: calcDiff2(preFqTargets, curTarget),
      qoqTargets: calcDiff2(preFqActuals, curTarget),
      qoqActuals: calcDiff2(preFqActuals, curActual),
    }

    const key = `jbp-planning-cells-q-sob-${currentCat}-${idx}-${originalQ}-${isUnits}-${isRollUp}-${mod}`

    return (
      <Fragment key={key}>
        {/* Prev FY (Calc) */}

        {isCalc && isShowCalcActuals && (
          <td
            style={{
              backgroundColor: getPlanningPeriodColumnColor(isPlanningByFY, isFuture, true),
            }}
            className={cn({
              [styles.colStrip]: !isActiveColumns,
              [styles.colSeparator]: isPlanningByFY,
              [styles.rollUp]: isRollUp,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                <div className={cn(styles.itemActual, styles.itemTarget)}>&nbsp;</div>
                <div className={styles.itemTarget}>&nbsp;</div>
                {/* YoY, QoQ */}
                <ExtraSobRowsTotal {...extraRowsProps} />
              </div>
            </div>
          </td>
        )}

        {/* Current FY */}

        {isCurr && (
          <td
            style={{
              backgroundColor: getPlanningPeriodColumnColor(isPlanningByFY, isFuture, true),
            }}
            className={cn(styles.colSeparator, {
              [styles.colStrip]: !isActiveColumns,
              [styles.colStripCurrentSob]: isCurrFY,
              [styles.colSeparator]: isShowSeparator,
              [styles.colSeparatorThin]: isShowSeparatorThin,
              [styles.rollUp]: isRollUp,
            })}
          >
            <div className={styles.itemWrapper}>
              <div className={styles.itemValues}>
                <div className={cn(styles.itemActual, styles.itemTarget)}>
                  {isShowActualsCell && (
                    <>
                      {isShowCurActCompletenessField ? (
                        <PartnersCompletenessField
                          translationTag={completenessTag}
                          partnersCompleteness={curActCompleteness}
                          onClick={onSoBCompletenessClick}
                          type="actuals"
                        />
                      ) : (
                        fieldWrapper(
                          curActual,
                          year,
                          isRetailer,
                          marketShareData,
                          'actuals',
                          isShowZero,
                          SOB_FRACTION_DIGITS
                        )
                      )}
                    </>
                  )}
                </div>
                <div className={styles.itemTarget}>
                  {isNoPlan && !isPrevYear ? (
                    <Translation text="jbp-planning-no-plan" span />
                  ) : (
                    <>
                      {isShowCurTarCompletenessField ? (
                        <PartnersCompletenessField
                          translationTag={completenessTag}
                          partnersCompleteness={curTarCompleteness}
                          onClick={onSoBCompletenessClick}
                          type="targets"
                        />
                      ) : (
                        <>
                          <>
                            {isHideCurrTargets ? (
                              <>&nbsp;</>
                            ) : (
                              fieldWrapper(
                                curTarget,
                                year,
                                isRetailer,
                                marketShareData,
                                'targets',
                                isShowZero,
                                SOB_FRACTION_DIGITS
                              )
                            )}
                          </>
                        </>
                      )}
                    </>
                  )}
                </div>
                {/* YoY, QoQ */}
                <ExtraSobRowsTotal {...extraRowsProps} />
              </div>
            </div>
          </td>
        )}

        {/*<td className={cn({ [styles.colStrip]: !isActiveColumns, [styles.rollUp]: isRollUp })}>
          <div className={styles.itemWrapper}>
            <div className={styles.itemIcon}>
              {isShowActualsCell && (
                <>
                  {isShowCurActCompletenessField ? (
                    <>-</>
                  ) : (
                    <>
                      <GrowField
                        percent={yoyActual}
                        fractionDigits={SOB_FRACTION_DIGITS}
                        isPaleColor={true}
                        diff
                      />
                    </>
                  )}
                </>
              )}
              <div>
                {isHideCurrTargetsYoY ? (
                  <span>&nbsp;</span>
                ) : (
                  <>
                    {isShowCurTarCompletenessField ? (
                      <>-</>
                    ) : (
                      <>
                        <GrowField percent={yoyTarget} fractionDigits={SOB_FRACTION_DIGITS} diff />
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </td>*/}
      </Fragment>
    )
  }

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    if (!props.urlActualsValueType) return

    const urlTypeLower = props.urlActualsValueType.toLowerCase()
    const urlType = Object.values(ActualsValueTypes).find((v) => v.toLowerCase() === urlTypeLower)
    if (!urlType || !actualsTypes.includes(urlType)) return

    onChangeActualsType(urlType)
  }, [props.urlActualsValueType, actualsTypes, onChangeActualsType])

  useEffect(() => {
    if ((partnerId && fyear && buttonCurrType) || props.composedReportMode) {
      preparePlanningData()
    }
  }, [
    partnerId,
    fyear,
    buttonCurrType,
    //props.dataSetInfo,
    isRegionalDistributor,
    props.isRegionalPlanning,
    props.planningData,
    props.globalCategory,
  ])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <table className={cn(stylesTable.table, stylesCompensation.table, styles.table)}>
        <caption>
          <WidgetHeaderWithHelpButton section={HelpSection.FinancialAndSOB}>
            {totalHeaderKey}
          </WidgetHeaderWithHelpButton>
          <LoaderOverlay show={!isLoaded} />

          {isShowCategoryNA && !isEditAllowed && isCategoryNA() && (
            <div>
              <div className={styles.captionCell}>
                <Translation text={'jbp-not-applicable-status-not-applicable'} />
              </div>
            </div>
          )}
        </caption>

        <thead>
          {!isCategoryNA() && (
            <tr>
              <th colSpan={planningColSpan}>
                <div className={styles.filtersContainer}>
                  <div className={styles.filtersRow}>
                    {isShowCurrencyCtrl && (
                      <>
                        <CurrenciesButtons />
                        <div className={styles.filterNextItemEmpty} />
                      </>
                    )}
                    {isShowNetNdpCtrl && (
                      <>
                        <ActualsTypeButtons />
                        <div className={styles.filterNextItemEmpty} />
                      </>
                    )}
                    {isShowSalesTypeCtrl && (
                      <>
                        <div className={styles.sellTypeNoWrap}>
                          <SellTypeButtons actualsType={actualsType} />
                        </div>
                      </>
                    )}
                    {isEnableRounding && (
                      <>
                        <div className={styles.filterNextItemEmpty} />
                        <Tooltip
                          inline
                          tipContent={() => <Translation span text="jbp-planning-rounding-tip" />}
                          horizontalStartPosition={TPopupHorizontal.Center}
                          horizontalDirection={TPopupHorizontal.Right}
                        >
                          <RowButtons
                            generateTranslationKey={(value) => value}
                            value={props.roundingValue}
                            options={ROUNDING_VALUES}
                            onOptionClick={props.setRoundingValue}
                          />
                        </Tooltip>
                        {isRetailer && (
                          <>
                            <div className={styles.filterNextItemEmpty} />
                            <div className={styles.optionSwitcherContainer}>
                              <RowButtons
                                value={isShowDetailedPlanning ? 'Hierarchical' : 'Flat'}
                                options={DISPLAY_OPTIONS}
                                generateTranslationKey={(val) => generateTranslationKey(val)}
                                onOptionClick={() =>
                                  props.displaySettings.actions.setIsShowDetailedPlanning(
                                    !isShowDetailedPlanning
                                  )
                                }
                              />
                            </div>
                          </>
                        )}
                        {isExistControlsAfterRounding && (
                          <div className={styles.filterNextItemEmpty}></div>
                        )}
                      </>
                    )}
                    {isShowActualsCtrl && !IS_ALWAYS_SHOW_ACTUALS && (
                      <div className={cn(styles.filterItem)} style={{ color: actualsColor }}>
                        <Checkbox
                          checked={props.showActuals}
                          label={<Translation text={'jbp-partner-actuals-show-actuals'} span />}
                          onChange={(val) => props.setShowActuals(!!val)}
                        />
                      </div>
                    )}
                    {isDistributor && isRegionalDistributor && (
                      <Checkbox
                        className={cn(styles.filterItem, {
                          [styles.filterNextItem]: !isShowDistributorCtrlFirst,
                        })}
                        checked={props.isRegionalPlanning}
                        label={<Translation text={'jbp-distributor-show-regional-planning'} span />}
                        onChange={(val) => props.setIsRegionalPlanning(!!val)}
                      />
                    )}
                    <div
                      className={cn(styles.switchersContainer, {
                        [styles.switchersContainerFull]: props.isOfferingPlanningCompleted,
                      })}
                    >
                      {isShowContractualSwitcher && (
                        <div className={styles.switcher}>
                          <Switch
                            padding
                            checked={isUseContractuals}
                            onChange={() =>
                              props.setIsContractualPlanning &&
                              props.setIsContractualPlanning(!isUseContractuals)
                            }
                          />
                          <div className={styles.switcherText}>
                            <Translation span text="jbp-planning-contractual-switcher" />
                          </div>
                        </div>
                      )}
                      {isShowOnlyCompletedPlansSwitcher && (
                        <div className={styles.switcher}>
                          <Switch
                            padding
                            checked={props.isShowOnlyCompletedPlans}
                            onChange={() =>
                              props.setIsShowOnlyCompletedPlans &&
                              props.setIsShowOnlyCompletedPlans(!props.isShowOnlyCompletedPlans)
                            }
                          />
                          <div className={styles.switcherText}>
                            <Translation span text="jbp-planning-show-only-completed-plans" />
                          </div>
                        </div>
                      )}
                      {isShowPrevTargetsCtrl && (
                        <div className={cn(styles.filterItem, styles.filterNextItem)}>
                          <Checkbox
                            checked={isShowPrevTargets}
                            onChange={() => setIsShowPrevTargets((val) => !val)}
                            label={
                              <Translation
                                span
                                text="jbp-planning-show-prev-targets"
                                replaceParams={showPrevTargetsParams}
                              />
                            }
                          />
                        </div>
                      )}
                      {!isDistributor && !!props.isOfferingPlanningCompleted && (
                        <div className={styles.smbEuSlot}>
                          <PlanningSmbSwitcher
                            isVisible
                            formats={props.planningSmbFormats ?? props.formats}
                            planningData={props.planningData}
                            planningSmb={props.planningSmbData || []}
                            partnerId={partnerId}
                            partnerName={partnerInfo?.PartnerName || partnerId}
                            isDistributor={isDistributor}
                            fYear={fyear}
                            programType={props.dataSetInfo.ProgramType || buttonCurrType}
                            sectionSettings={sectionSettings}
                            globalCategory={props.globalCategory ? globalCategoryKey : undefined}
                            planActualsType={currentValueType}
                            planCurrency={currency}
                            actualsTypes={actualsTypes}
                            currencies={availableCurrencies}
                            roundingValue={props.roundingValue}
                            roundingOptions={ROUNDING_VALUES}
                            onRoundingChange={(value) =>
                              props.setRoundingValue(value as PlanningRounding)
                            }
                            isQuarterlyPlanning={isPlanningSmbQuarterly}
                            isQuarterlyPlanningDisabled={isOnlyQuarterlyPlanning}
                            isEditMode={isEditMode}
                            isEditAllowed={
                              isEditAllowed && props.isPlanningSmbEditAllowed !== false
                            }
                            formatMoney={formatPlanningSmbMoney}
                            onSaved={() => props.onReloadPlanningData?.()}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </th>
            </tr>
          )}

          {!isCategoryNA() && (
            <>
              <tr>
                <th colSpan={planningColSpan}>
                  {props.showActuals && (
                    <div className={styles.infoContainer}>
                      <i
                        className={cn('kbase kbase-info-alt', styles.infoIcon)}
                        aria-hidden="true"
                      />
                      <span className={styles.actualsLegend}>
                        <Translation text="jbp-planning-actuals-legend" actionsInside />
                      </span>
                      <span className={styles.targetsLegend}>
                        <Translation text="jbp-planning-targets-legend" actionsInside />
                      </span>
                    </div>
                  )}
                  <div className={styles.infoContainer}>
                    {!props.showActuals && (
                      <i
                        className={cn('kbase kbase-info-alt', styles.infoIcon)}
                        aria-hidden="true"
                      />
                    )}
                    <Translation
                      text={
                        props.showActuals
                          ? 'jbp-planning-targets-info'
                          : 'jbp-planning-actuals-info'
                      }
                      actionsInside
                    />
                  </div>
                </th>
              </tr>
              {isUseContractuals && (
                <SplitPlanningRow
                  splitType={splitType}
                  setSplitType={setSplitType}
                  isShowAllSplits={isShowAllSplits}
                  setIsShowAllSplits={setIsShowAllSplits}
                  isDistributorPlanner={props.isDistributorPlanner}
                  colSpan={planningColSpan}
                />
              )}
              {!isCategoryNA() && (
                <CategoryHeader
                  translationKey="jbp-planning-total-header"
                  fyear={fyear}
                  isYearlyPlanning={isYearlyPlanning}
                  isShowCalcActuals={isShowCalcActuals}
                  isShowFutureYears={isShowFutureYears}
                  currentFQuarter={jbpSettings.currentFQuarter}
                  periodFQuarters={fQuarters}
                  isQbrYtd={props.isQuarterlyBusinessReviewMode}
                />
              )}
            </>
          )}
        </thead>

        {!isCategoryNA() && isDataLoaded && (
          <tbody>
            {[OVERALL_CATEGORY].map((format, idx) => {
              const subCat = format
              const isOverall = subCat === OVERALL_CATEGORY || !subCat

              const uniqueCategory = isOverall ? currentCat : subCat
              const uniqueKey = `jbp-planning-contractual-${uniqueCategory}`

              const tip = getPopupComment(idx)

              if (isCollapsed && !isOverall) {
                return <></>
              }

              return (
                <Fragment key={`jbp-target-actuals-total-rows-${uniqueKey}-${idx}`}>
                  {isShowTotal && (
                    <tr
                      key={`jbp-target-actuals-total-${uniqueKey}-${idx}`}
                      className={cn({ [styles.contractualTotalRow]: isUseContractuals })}
                    >
                      <td className={cn(styles.totalCell, styles.catTitle)}>
                        <div className={styles.categoryTitle}>
                          <Tooltip
                            inline
                            tipContent={() => (
                              <div className={styles.productLineTip}>
                                {totalTipKey ? (
                                  <Translation text={totalTipKey} span actionsInside />
                                ) : (
                                  <span>{tip}</span>
                                )}
                              </div>
                            )}
                            horizontalStartPosition={TPopupHorizontal.Right}
                            horizontalDirection={TPopupHorizontal.Right}
                          >
                            <div className={cn(styles.productLine, styles.noWrap)}>
                              <Translation text={totalKey} span actionsInside />
                            </div>
                          </Tooltip>
                          <CategorySubTitle
                            isShowSubTitles={isOverall && !props.composedReportMode}
                            isShowQuotas={false}
                            isShowActuals={props.showActuals}
                            isShowActualsSplit={isShowActualsSplit}
                            isShowSmbVsEuSplit={isShowSmbVsEuSplit}
                            isShowYoYActualsVsActuals={isShowYoYActualsVsActuals}
                            isShowYoYActualsVsPlans={isShowYoYActualsVsPlans}
                            isShowYoYPlansVsPlans={isShowYoYPlansVsPlans}
                            isShowYoYQuotaVsActuals={false}
                            isShowYoYPartnersVsPartners={isShowYoYPartnersVsPartners}
                            isShowPlanAchievement={isShowPlanAchievement}
                            isShowQuotaAchievement={false}
                            isShowQoQActualsVsActuals={isShowQoQActualsVsActuals}
                            isShowQoQActualsVsPlans={isShowQoQActualsVsPlans}
                            isShowQoQPlansVsPlans={isShowQoQPlansVsPlans}
                            isShowQoQQuotaVsActuals={false}
                            isShowQoQPartnersVsPartners={isShowQoQPartnersVsPartners}
                            isDistributorPlanner={props.isDistributorPlanner}
                            isShowPartnerCount={isShowPartnerCount}
                            isEditRow={false}
                            sType={sType}
                          />
                        </div>
                      </td>
                      {renderQ(idx, -fyFq, isEditAllowed, false, false, 'prevPrev')}

                      {isYearlyPlanning ? (
                        <></>
                      ) : (
                        fQuarters.map((q) => renderQ(idx, -q, isEditAllowed, false, false, 'prev'))
                      )}
                      {renderQ(idx, -fyFq, isEditAllowed, false, false, 'prev')}
                      {renderQ(idx, -fyFq, isEditAllowed, false, false, 'calc')}

                      {isYearlyPlanning ? (
                        <></>
                      ) : (
                        fQuarters.map((q) => renderQ(idx, q, isEditAllowed, false, false, 'curr'))
                      )}

                      {renderQ(idx, fyFq, isEditAllowed, false, false, 'curr')}
                      {isShowFutureYears &&
                        renderQ(idx, fyFq + 1, isEditAllowed, false, false, 'futr')}
                      {isShowFutureYears &&
                        renderQ(idx, fyFq + 2, isEditAllowed, false, false, 'futr')}
                    </tr>
                  )}

                  {!!partnerInfo?.IsGlobal && (
                    <tr
                      key={`jbp-target-rollup-${subCat}-${idx}`}
                      className={cn(styles.contractualRow, styles.rollupRow)}
                    >
                      <td className={cn(styles.catTitle)}>
                        <div className={styles.categoryTitle}>
                          <div className={cn(styles.noWrap)}>
                            <Translation text="jbp-planning-rollup" span actionsInside />
                          </div>
                          <CategorySubTitle
                            isShowSubTitles={isOverall && !props.composedReportMode}
                            isShowQuotas={false}
                            isShowActuals={props.showActuals}
                            isShowActualsSplit={isShowActualsSplit}
                            isShowSmbVsEuSplit={isShowSmbVsEuSplit}
                            isShowYoYActualsVsActuals={isShowYoYActualsVsActuals}
                            isShowYoYActualsVsPlans={isShowYoYActualsVsPlans}
                            isShowYoYPlansVsPlans={isShowYoYPlansVsPlans}
                            isShowYoYQuotaVsActuals={false}
                            isShowYoYPartnersVsPartners={isShowYoYPartnersVsPartners}
                            isShowPlanAchievement={isShowPlanAchievement}
                            isShowQuotaAchievement={false}
                            isShowQoQActualsVsActuals={isShowQoQActualsVsActuals}
                            isShowQoQActualsVsPlans={isShowQoQActualsVsPlans}
                            isShowQoQPlansVsPlans={isShowQoQPlansVsPlans}
                            isShowQoQQuotaVsActuals={false}
                            isShowQoQPartnersVsPartners={isShowQoQPartnersVsPartners}
                            isDistributorPlanner={props.isDistributorPlanner}
                            isShowPartnerCount={isShowPartnerCount}
                            isEditRow={false}
                            sType={sType}
                          />
                        </div>
                      </td>
                      {renderQ(idx, -fyFq, isEditAllowed, false, true, 'prevPrev')}

                      {isYearlyPlanning ? (
                        <></>
                      ) : (
                        fQuarters.map((q) => renderQ(idx, -q, isEditAllowed, false, true, 'prev'))
                      )}
                      {renderQ(idx, -fyFq, isEditAllowed, false, true, 'prev')}
                      {renderQ(idx, -fyFq, isEditAllowed, false, true, 'calc')}

                      {isYearlyPlanning ? (
                        <></>
                      ) : (
                        fQuarters.map((q) => renderQ(idx, q, isEditAllowed, false, true, 'curr'))
                      )}

                      {renderQ(idx, fyFq, isEditAllowed, false, true, 'curr')}
                      {isShowFutureYears &&
                        renderQ(idx, fyFq + 1, isEditAllowed, false, true, 'futr')}
                      {isShowFutureYears &&
                        renderQ(idx, fyFq + 2, isEditAllowed, false, true, 'futr')}
                    </tr>
                  )}

                  {isUseContractuals && (
                    <Fragment key={`${uniqueKey}-${idx}-contractuals`}>
                      {isShowContr && (
                        <tr
                          key={`${uniqueKey}-${idx}-contractual`}
                          className={styles.contractualRow}
                        >
                          <td className={styles.catTitle}>
                            <div className={styles.categoryTitle}>
                              <Translation
                                span
                                text={
                                  props.isDistributorPlanner
                                    ? 'jbp-planning-contractual-non-power'
                                    : 'jbp-planning-contractual-contractual'
                                }
                              />
                              <CategorySubTitle
                                isShowSubTitles={isOverall && !props.composedReportMode}
                                isShowQuotas={false}
                                isShowActuals={props.showActuals}
                                isShowActualsSplit={isShowActualsSplit}
                                isShowSmbVsEuSplit={isShowSmbVsEuSplit}
                                isShowYoYActualsVsActuals={isShowYoYActualsVsActuals}
                                isShowYoYActualsVsPlans={isShowYoYActualsVsPlans}
                                isShowYoYPlansVsPlans={isShowYoYPlansVsPlans}
                                isShowYoYQuotaVsActuals={false}
                                isShowYoYPartnersVsPartners={isShowYoYPartnersVsPartners}
                                isShowPlanAchievement={isShowPlanAchievement}
                                isShowQuotaAchievement={false}
                                isShowQoQActualsVsActuals={isShowQoQActualsVsActuals}
                                isShowQoQActualsVsPlans={isShowQoQActualsVsPlans}
                                isShowQoQPlansVsPlans={isShowQoQPlansVsPlans}
                                isShowQoQQuotaVsActuals={false}
                                isShowQoQPartnersVsPartners={isShowQoQPartnersVsPartners}
                                isDistributorPlanner={props.isDistributorPlanner}
                                isShowPartnerCount={isShowPartnerCount}
                                isEditRow={false}
                                sType={'c'}
                              />
                            </div>
                          </td>
                          {renderQContr(idx, -fyFq, false, 'c', 'prevPrev')}

                          {isYearlyPlanning ? (
                            <></>
                          ) : (
                            fQuarters.map((q) => renderQContr(idx, -q, false, 'c', 'prev'))
                          )}
                          {renderQContr(idx, -fyFq, false, 'c', 'prev')}
                          {renderQContr(idx, -fyFq, false, 'c', 'calc')}

                          {isYearlyPlanning ? (
                            <></>
                          ) : (
                            fQuarters.map((q) => renderQContr(idx, q, false, 'c', 'curr'))
                          )}

                          {renderQContr(idx, fyFq, false, 'c', 'curr')}
                          {isShowFutureYears && renderQContr(idx, fyFq + 1, false, 'c', 'futr')}
                          {isShowFutureYears && renderQContr(idx, fyFq + 2, false, 'c', 'futr')}
                        </tr>
                      )}
                      {isShowTrans && (
                        <tr
                          key={`${uniqueKey}-${idx}-transactional`}
                          className={styles.contractualRow}
                        >
                          <td className={styles.catTitle}>
                            <Translation span text="" />
                            <div className={styles.categoryTitle}>
                              <Translation
                                span
                                text={
                                  props.isDistributorPlanner
                                    ? 'jbp-planning-contractual-power'
                                    : 'jbp-planning-contractual-transactional'
                                }
                              />
                              <CategorySubTitle
                                isShowSubTitles={isOverall && !props.composedReportMode}
                                isShowQuotas={false}
                                isShowActuals={props.showActuals}
                                isShowActualsSplit={isShowActualsSplit}
                                isShowSmbVsEuSplit={isShowSmbVsEuSplit}
                                isShowYoYActualsVsActuals={isShowYoYActualsVsActuals}
                                isShowYoYActualsVsPlans={isShowYoYActualsVsPlans}
                                isShowYoYPlansVsPlans={isShowYoYPlansVsPlans}
                                isShowYoYQuotaVsActuals={false}
                                isShowYoYPartnersVsPartners={isShowYoYPartnersVsPartners}
                                isShowPlanAchievement={isShowPlanAchievement}
                                isShowQuotaAchievement={false}
                                isShowQoQActualsVsActuals={isShowQoQActualsVsActuals}
                                isShowQoQActualsVsPlans={isShowQoQActualsVsPlans}
                                isShowQoQPlansVsPlans={isShowQoQPlansVsPlans}
                                isShowQoQQuotaVsActuals={false}
                                isShowQoQPartnersVsPartners={isShowQoQPartnersVsPartners}
                                isDistributorPlanner={props.isDistributorPlanner}
                                isShowPartnerCount={isShowPartnerCount}
                                isEditRow={false}
                                sType={'t'}
                              />
                            </div>
                          </td>
                          {renderQContr(idx, -fyFq, false, 't', 'prevPrev')}

                          {isYearlyPlanning ? (
                            <></>
                          ) : (
                            fQuarters.map((q) => renderQContr(idx, -q, false, 't', 'prev'))
                          )}
                          {renderQContr(idx, -fyFq, false, 't', 'prev')}
                          {renderQContr(idx, -fyFq, false, 't', 'calc')}

                          {isYearlyPlanning ? (
                            <></>
                          ) : (
                            fQuarters.map((q) => renderQContr(idx, q, false, 't', 'curr'))
                          )}

                          {renderQContr(idx, fyFq, false, 't', 'curr')}
                          {isShowFutureYears && renderQContr(idx, fyFq + 1, false, 't', 'futr')}
                          {isShowFutureYears && renderQContr(idx, fyFq + 2, false, 't', 'futr')}
                        </tr>
                      )}
                    </Fragment>
                  )}

                  {isShowSoB && (
                    <tr
                      key={`jbp-planning-total-sob-${subCat}-${idx}`}
                      className={cn(styles.contractualRow, styles.sobRow)}
                    >
                      <td className={styles.catTitle}>
                        <div className={styles.categoryTitle}>
                          <div className={cn(styles.sobTitle, styles.noWrap)}>
                            <Translation text="jbp-planning-sob-row" />
                            <div className={styles.hintWrapper}>
                              <HintComponent
                                text="jbp-planning-sob-total-hint"
                                replaceParams={sobCalcCategoriesParams}
                              />
                            </div>
                          </div>
                          <CategorySubTitle
                            isShowSubTitles={isOverall && !props.composedReportMode}
                            isShowQuotas={false}
                            isShowActuals={props.showActuals}
                            isShowActualsSplit={false}
                            isShowSmbVsEuSplit={false}
                            isShowYoYActualsVsActuals={isShowYoYActualsVsActuals}
                            isShowYoYActualsVsPlans={isShowYoYActualsVsPlans}
                            isShowYoYPlansVsPlans={isShowYoYPlansVsPlans}
                            isShowYoYQuotaVsActuals={false}
                            isShowYoYPartnersVsPartners={isShowYoYPartnersVsPartners}
                            isShowPlanAchievement={isShowPlanAchievement}
                            isShowQuotaAchievement={false}
                            isShowQoQActualsVsActuals={isShowQoQActualsVsActuals}
                            isShowQoQActualsVsPlans={isShowQoQActualsVsPlans}
                            isShowQoQPlansVsPlans={isShowQoQPlansVsPlans}
                            isShowQoQQuotaVsActuals={false}
                            isShowQoQPartnersVsPartners={isShowQoQPartnersVsPartners}
                            isDistributorPlanner={props.isDistributorPlanner}
                            isShowPartnerCount={isShowPartnerCount}
                            isEditRow={false}
                            isSob
                            sType={sType}
                          />
                        </div>
                      </td>
                      {renderSoB(idx, -fyFq, false, false, false, 'prevPrev')}

                      {isYearlyPlanning ? (
                        <></>
                      ) : (
                        fQuarters.map((q) => renderSoB(idx, -q, false, false, false, 'prev'))
                      )}
                      {renderSoB(idx, -fyFq, false, false, false, 'prev')}
                      {renderSoB(idx, -fyFq, false, false, false, 'calc')}

                      {isYearlyPlanning ? (
                        <></>
                      ) : (
                        fQuarters.map((q) => renderSoB(idx, q, false, false, false, 'curr'))
                      )}

                      {renderSoB(idx, fyFq, false, false, false, 'curr')}
                      {isShowFutureYears && renderSoB(idx, fyFq + 1, false, false, false, 'futr')}
                      {isShowFutureYears && renderSoB(idx, fyFq + 2, false, false, false, 'futr')}
                    </tr>
                  )}

                  {isShowGlobalSoB && (
                    <tr
                      key={`jbp-planning-total-sob-rollup-${subCat}-${idx}`}
                      className={cn(styles.contractualRow, styles.rollupRow)}
                    >
                      <td className={styles.catTitle}>
                        <div className={styles.categoryTitle}>
                          <div className={cn(styles.sobTitle, styles.noWrap)}>
                            <Translation text="jbp-planning-sob-rollup" />
                            <div className={styles.hintWrapper}>
                              <HintComponent
                                text="jbp-planning-sob-total-hint"
                                replaceParams={sobCalcCategoriesParams}
                              />
                            </div>
                          </div>
                          <CategorySubTitle
                            isShowSubTitles={isOverall && !props.composedReportMode}
                            isShowQuotas={false}
                            isShowActuals={props.showActuals}
                            isShowActualsSplit={false}
                            isShowSmbVsEuSplit={false}
                            isShowYoYActualsVsActuals={isShowYoYActualsVsActuals}
                            isShowYoYActualsVsPlans={isShowYoYActualsVsPlans}
                            isShowYoYPlansVsPlans={isShowYoYPlansVsPlans}
                            isShowYoYQuotaVsActuals={false}
                            isShowYoYPartnersVsPartners={isShowYoYPartnersVsPartners}
                            isShowPlanAchievement={isShowPlanAchievement}
                            isShowQuotaAchievement={false}
                            isShowQoQActualsVsActuals={isShowQoQActualsVsActuals}
                            isShowQoQActualsVsPlans={isShowQoQActualsVsPlans}
                            isShowQoQPlansVsPlans={isShowQoQPlansVsPlans}
                            isShowQoQQuotaVsActuals={false}
                            isShowQoQPartnersVsPartners={isShowQoQPartnersVsPartners}
                            isDistributorPlanner={props.isDistributorPlanner}
                            isShowPartnerCount={isShowPartnerCount}
                            isEditRow={false}
                            isSob
                            sType={sType}
                          />
                        </div>
                      </td>
                      {renderSoB(idx, -fyFq, false, false, true, 'prevPrev')}

                      {isYearlyPlanning ? (
                        <></>
                      ) : (
                        fQuarters.map((q) => renderSoB(idx, -q, false, false, true, 'prev'))
                      )}
                      {renderSoB(idx, -fyFq, false, false, true, 'prev')}
                      {renderSoB(idx, -fyFq, false, false, true, 'calc')}

                      {isYearlyPlanning ? (
                        <></>
                      ) : (
                        fQuarters.map((q) => renderSoB(idx, q, false, false, true, 'curr'))
                      )}

                      {renderSoB(idx, fyFq, false, false, true, 'curr')}
                      {isShowFutureYears && renderSoB(idx, fyFq + 1, false, false, true, 'futr')}
                      {isShowFutureYears && renderSoB(idx, fyFq + 2, false, false, true, 'futr')}
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        )}
      </table>
    </div>
  )
}
